import React, { useMemo, useState } from 'react'
import { ModalBase } from '@/components/modals/ModalBase'
import { checkoutService, BulkRecipient } from '@/services/checkoutService'

type Step = 'quantity' | 'recipients' | 'channel' | 'review'

interface Props {
  isOpen: boolean
  onClose: () => void
  course: { id: string; title: string; price: number }
  batchId?: string
  buyer: { email: string; name: string; phone?: string }
}

const CHANNELS: Array<{ value: string; label: string }> = [
  { value: 'qris', label: 'QRIS' },
  { value: 'va_bca', label: 'Virtual Account BCA' },
  { value: 'va_bri', label: 'Virtual Account BRI' },
  { value: 'va_mandiri', label: 'Virtual Account Mandiri' },
  { value: 'ewallet_gopay', label: 'GoPay' },
  { value: 'ewallet_ovo', label: 'OVO' },
  { value: 'ewallet_dana', label: 'DANA' },
]

/**
 * BulkCheckoutModal — M5 bulk purchase flow.
 *
 * Stepper:
 *  1. Quantity — pick N (1..50), preview total.
 *  2. Recipients — N rows of (Name, Email). Row 1 is the buyer (email readonly).
 *  3. Channel — pick a payment channel.
 *  4. Review — confirm + submit.
 *
 * On submit we POST /checkout/bulk and redirect to the Pivot payment URL.
 */
export const BulkCheckoutModal: React.FC<Props> = ({ isOpen, onClose, course, batchId, buyer }) => {
  const [step, setStep] = useState<Step>('quantity')
  const [quantity, setQuantity] = useState(1)
  const [recipients, setRecipients] = useState<BulkRecipient[]>([
    { name: buyer.name, email: buyer.email },
  ])
  const [channel, setChannel] = useState<string>('qris')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const total = useMemo(() => course.price * quantity, [course.price, quantity])

  const updateQuantity = (q: number) => {
    if (q < 1) q = 1
    if (q > 50) q = 50
    setQuantity(q)
    setRecipients((prev) => {
      const next = [...prev]
      while (next.length < q) next.push({ name: '', email: '' })
      while (next.length > q) next.pop()
      // Slot 0 always the buyer.
      next[0] = { name: buyer.name, email: buyer.email }
      return next
    })
  }

  const setRecipient = (idx: number, patch: Partial<BulkRecipient>) => {
    setRecipients((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  const validateRecipients = (): string | null => {
    const seen = new Set<string>()
    for (let i = 0; i < recipients.length; i++) {
      const em = recipients[i].email.trim().toLowerCase()
      if (!em) return `Email baris ${i + 1} wajib diisi`
      if (seen.has(em)) return `Email duplikat: ${em}`
      seen.add(em)
      // Allow buyer email only in slot 0.
      if (em === buyer.email.toLowerCase() && i !== 0) {
        return 'Email pembeli tidak boleh dipakai di slot lain'
      }
    }
    return null
  }

  const handleNext = () => {
    setError(null)
    if (step === 'quantity') {
      setStep('recipients')
      return
    }
    if (step === 'recipients') {
      const err = validateRecipients()
      if (err) {
        setError(err)
        return
      }
      setStep('channel')
      return
    }
    if (step === 'channel') {
      setStep('review')
      return
    }
  }

  const handleBack = () => {
    setError(null)
    if (step === 'recipients') setStep('quantity')
    else if (step === 'channel') setStep('recipients')
    else if (step === 'review') setStep('channel')
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const resp = await checkoutService.createBulkOrder({
        courseId: course.id,
        batchId,
        paymentChannel: channel,
        customerName: buyer.name,
        customerPhone: buyer.phone,
        recipients,
      })
      if (resp.paymentUrl) {
        window.location.href = resp.paymentUrl
      } else {
        // No Pivot URL (dev/test) — close + no-op.
        onClose()
      }
    } catch (e) {
      setError((e as Error).message || 'Gagal membuat order')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ModalBase isOpen={isOpen} onClose={onClose}>
      <h2 className="text-xl font-semibold mb-2">Pembelian Bulk — {course.title}</h2>
      <p className="text-sm text-gray-500 mb-4">Langkah {['quantity', 'recipients', 'channel', 'review'].indexOf(step) + 1} dari 4</p>

      {step === 'quantity' && (
        <div data-testid="bulk-step-quantity" className="space-y-3">
          <label className="block text-sm font-medium">Jumlah tiket</label>
          <input
            type="number"
            min={1}
            max={50}
            value={quantity}
            onChange={(e) => updateQuantity(parseInt(e.target.value, 10) || 1)}
            className="w-full border rounded px-3 py-2"
          />
          <p className="text-sm text-gray-600">Total: Rp {total.toLocaleString('id-ID')}</p>
        </div>
      )}

      {step === 'recipients' && (
        <div data-testid="bulk-step-recipients" className="space-y-3">
          {recipients.map((r, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder={`Nama ${idx === 0 ? '(Anda)' : `#${idx + 1}`}`}
                value={r.name || ''}
                onChange={(e) => setRecipient(idx, { name: e.target.value })}
                className="border rounded px-3 py-2"
                disabled={idx === 0}
              />
              <input
                type="email"
                placeholder="Email"
                value={r.email}
                onChange={(e) => setRecipient(idx, { email: e.target.value })}
                className="border rounded px-3 py-2"
                disabled={idx === 0}
              />
            </div>
          ))}
        </div>
      )}

      {step === 'channel' && (
        <div data-testid="bulk-step-channel" className="space-y-2">
          {CHANNELS.map((c) => (
            <label key={c.value} className="flex items-center gap-2">
              <input
                type="radio"
                checked={channel === c.value}
                onChange={() => setChannel(c.value)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      )}

      {step === 'review' && (
        <div data-testid="bulk-step-review" className="space-y-3 text-sm">
          <h3 className="font-medium">Konfirmasi pesanan</h3>
          <p>Total tiket: {quantity}</p>
          <p>Total bayar: Rp {total.toLocaleString('id-ID')}</p>
          <ul className="list-disc pl-5">
            {recipients.map((r, idx) => (
              <li key={idx}>
                {idx === 0 ? '(Anda) ' : ''}
                {r.name ? r.name + ' — ' : ''}
                {r.email}
              </li>
            ))}
          </ul>
          <p>Channel: {channel}</p>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 mt-3" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-between gap-2 mt-4">
        <button
          type="button"
          onClick={step === 'quantity' ? onClose : handleBack}
          className="px-4 py-2 border rounded"
        >
          {step === 'quantity' ? 'Batal' : 'Kembali'}
        </button>
        {step !== 'review' ? (
          <button
            type="button"
            onClick={handleNext}
            className="px-4 py-2 bg-primary text-white rounded"
          >
            Lanjut
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-primary text-white rounded disabled:opacity-50"
          >
            {submitting ? 'Memproses...' : 'Bayar'}
          </button>
        )}
      </div>
    </ModalBase>
  )
}

export default BulkCheckoutModal
